import React, { useState, useEffect } from 'react';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import { fetchAdmins, deleteAdmin, createAdmin, fetchRoles, updateAdminRole, updateAdmin, fetchPermissions, fetchAdminPermissions, updatePermissions } from '../services/adminService';
import PageNavigator from './PageNavigator';
import styles from './ManageAdmins.module.css';

const ManageAdmins = ({ onBack }) => {
    const { hasCreateAdmins, hasUpdatePermission } = useCachedPermissions();

    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [deletingAdminId, setDeletingAdminId] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        phone_code: '+20',
        phone: '',
        role_id: ''
    });
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 15,
        results: 0,
        last_page: 1
    });
    const [showRoleDialog, setShowRoleDialog] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState('');
    const [roleLoading, setRoleLoading] = useState(false);
    const [roleError, setRoleError] = useState('');
    const [editFormData, setEditFormData] = useState({
        name: '',
        email: '',
        password: '',
        role_id: ''
    });
    const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
    const [allPermissions, setAllPermissions] = useState([]);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [permissionsLoading, setPermissionsLoading] = useState(false);
    const [permissionsError, setPermissionsError] = useState('');

    useEffect(() => {
        fetchAdminsData();
    }, [currentPage]);

    useEffect(() => {
        fetchRolesData();
        fetchAllPermissions();
    }, []);

    const fetchAllPermissions = async () => {
        try {
            const response = await fetchPermissions();
            if (response.status === 'success' && response.data && response.data.data) {
                setAllPermissions(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch permissions:', error);
        }
    };

    const fetchRolesData = async () => {
        try {
            const response = await fetchRoles();
            if (response.status === 'success' && response.data && response.data.data) {
                setRoles(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch roles:', error);
        }
    };

    const fetchAdminsData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchAdmins(currentPage, 15);

            if (response.status === 'success' && response.data) {
                setAdmins(response.data.data || []);
                setPagination({
                    total: response.data.total || 0,
                    page: response.data.page || 1,
                    limit: response.data.limit || 15,
                    results: response.data.results || 0,
                    last_page: response.data.last_page || 1
                });
                setTotalPages(response.data.last_page || 1);
            } else {
                throw new Error(response.message || 'Failed to fetch admins');
            }
        } catch (err) {
            console.error('Error fetching admins:', err);
            setError(err.message || 'Failed to fetch admins');
            setAdmins([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddAdmin = () => {
        setShowAddForm(true);
        setFormError('');
    };

    const handleCloseForm = () => {
        setShowAddForm(false);
        setFormData({
            name: '',
            email: '',
            password: '',
            phone_code: '+20',
            phone: '',
            role_id: ''
        });
        setFormError('');
    };

    const handleFormInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        setFormError('');
    };

    const handleSubmitForm = async (e) => {
        e.preventDefault();

        // Basic validation
        if (!formData.name || !formData.email || !formData.password || !formData.phone || !formData.role_id) {
            setFormError('Please fill in all required fields');
            return;
        }

        if (formData.password.length < 6) {
            setFormError('Password must be at least 6 characters long');
            return;
        }

        setFormLoading(true);
        setFormError('');

        try {
            await createAdmin(formData);

            // Show success message
            setSuccessMessage(`Admin "${formData.name}" has been successfully created.`);

            // Close form and reset
            handleCloseForm();

            // Refresh admins list
            fetchAdminsData();

            // Clear success message after 3 seconds
            setTimeout(() => {
                setSuccessMessage('');
            }, 3000);

        } catch (err) {
            console.error('Error creating admin:', err);
            setFormError(err.message || 'Failed to create admin');
        } finally {
            setFormLoading(false);
        }
    };

    const handleAlterPermissions = async () => {
        setPermissionsLoading(true);
        setPermissionsError('');

        try {
            // Use fixed role_id as specified
            const roleId = "6884985f6b41c86db0459611";
            const response = await fetchAdminPermissions(roleId);

            if (response.status === 'success' && response.data && response.data.data) {
                // Extract permission names from the response
                const adminPermissions = response.data.data.map(p => p.permission);
                setSelectedPermissions(adminPermissions);
                setShowPermissionsDialog(true);
            }
        } catch (error) {
            console.error('Failed to fetch admin permissions:', error);
            setPermissionsError(error.message || 'Failed to load permissions');
        } finally {
            setPermissionsLoading(false);
        }
    };

    const handleClosePermissionsDialog = () => {
        setShowPermissionsDialog(false);
        setSelectedPermissions([]);
        setPermissionsError('');
    };

    const handlePermissionChange = (permissionName, isChecked) => {
        setSelectedPermissions(prev => {
            if (isChecked) {
                return [...prev, permissionName];
            } else {
                return prev.filter(p => p !== permissionName);
            }
        });
        setPermissionsError('');
    };

    const handleSavePermissions = async () => {
        setPermissionsLoading(true);
        setPermissionsError('');

        try {
            // Convert selected permission names to permission IDs
            const selectedPermissionIds = allPermissions
                .filter(permission => selectedPermissions.includes(permission.name))
                .map(permission => permission._id);

            // Call the API to update permissions
            await updatePermissions(selectedPermissionIds);

            setSuccessMessage('Permissions updated successfully');
            handleClosePermissionsDialog();

            setTimeout(() => {
                setSuccessMessage('');
            }, 3000);
        } catch (error) {
            console.error('Failed to update permissions:', error);
            setPermissionsError(error.message || 'Failed to update permissions');
        } finally {
            setPermissionsLoading(false);
        }
    };

    const handleDeleteAdmin = async (adminId, adminName) => {
        // Show confirmation alert
        const confirmDelete = window.confirm(
            `Are you sure you want to delete admin "${adminName}"? This action cannot be undone.`
        );

        if (!confirmDelete) {
            return;
        }

        setDeletingAdminId(adminId);
        setError(null);

        try {
            await deleteAdmin(adminId);

            // Show success message
            setSuccessMessage(`Admin "${adminName}" has been successfully deleted.`);

            // Remove admin from the list
            setAdmins(prevAdmins => prevAdmins.filter(admin => admin._id !== adminId));

            // Update pagination counts
            setPagination(prev => ({
                ...prev,
                total: prev.total - 1,
                results: prev.results - 1
            }));

            // Clear success message after 3 seconds
            setTimeout(() => {
                setSuccessMessage('');
            }, 3000);

        } catch (err) {
            console.error('Error deleting admin:', err);
            setError(err.message || 'Failed to delete admin');
        } finally {
            setDeletingAdminId(null);
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleChangeRole = (admin) => {
        setSelectedAdmin(admin);

        // Populate edit form with current admin data
        setEditFormData({
            name: admin.name || '',
            email: admin.email || '',
            password: '', // Always empty for security
            role_id: admin.role_id || ''
        });

        // Use role_id if available, fallback to trying to find by role name
        if (admin.role_id) {
            setSelectedRole(admin.role_id);
        } else if (admin.role && roles.length > 0) {
            const role = roles.find(r => r.name.toLowerCase() === admin.role.toLowerCase());
            setSelectedRole(role ? role._id : '');
        } else {
            setSelectedRole('');
        }
        setShowRoleDialog(true);
        setRoleError('');
    };

    const handleCloseRoleDialog = () => {
        setShowRoleDialog(false);
        setSelectedAdmin(null);
        setSelectedRole('');
        setEditFormData({
            name: '',
            email: '',
            password: '',
            role_id: ''
        });
        setRoleError('');
    };

    const handleEditFormChange = (field, value) => {
        setEditFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleRoleChange = (roleId) => {
        setSelectedRole(roleId);
        setRoleError('');
    };

    const handleSaveRole = async () => {
        if (!selectedRole) {
            setRoleError('Please select a role');
            return;
        }

        if (!editFormData.name.trim()) {
            setRoleError('Name is required');
            return;
        }

        if (!editFormData.email.trim()) {
            setRoleError('Email is required');
            return;
        }

        setRoleLoading(true);
        setRoleError('');

        try {
            const adminData = {
                name: editFormData.name,
                email: editFormData.email,
                role_id: selectedRole
            };

            // Only include password if it's not empty
            if (editFormData.password.trim()) {
                adminData.password = editFormData.password;
            }

            await updateAdmin(selectedAdmin._id, adminData);
            setSuccessMessage(`Admin updated successfully for ${selectedAdmin.name}`);
            await fetchAdminsData();
            handleCloseRoleDialog();

            setTimeout(() => {
                setSuccessMessage('');
            }, 3000);
        } catch (error) {
            console.error('Failed to update admin:', error);
            setRoleError(error.message || 'Failed to update admin');
        } finally {
            setRoleLoading(false);
        }
    };

    const getRoleDisplay = (admin) => {
        // If admin has role_id, find the role name from roles array
        if (admin.role_id && roles.length > 0) {
            const role = roles.find(r => r._id === admin.role_id);
            return role ? role.name : 'Unknown';
        }

        // Fallback to the old role field for backwards compatibility
        switch (admin.role) {
            case 'super_admin':
                return 'Super Admin';
            case 'admin':
                return 'Admin';
            default:
                return admin.role || 'Unknown';
        }
    };

    const getRoleBadgeClass = (admin) => {
        // Get the role name first
        const roleName = getRoleDisplay(admin);

        switch (roleName.toLowerCase()) {
            case 'super_admin':
            case 'super admin':
                return styles.superAdminBadge;
            case 'admin':
                return styles.adminBadge;
            default:
                return styles.defaultBadge;
                return styles.defaultBadge;
        }
    };

    if (loading && admins.length === 0) {
        return (
            <div className={styles.manageAdminsContainer}>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <button onClick={onBack} className={styles.backButton}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5M12 19l-7-7 7-7" />
                            </svg>
                            Back
                        </button>
                        <h2 className={styles.headerTitle}>Manage Admins</h2>
                    </div>
                </div>
                <div className={styles.loadingState}>
                    <div className={styles.loadingSpinner}></div>
                    <p>Loading admins...</p>
                </div>
            </div>
        );
    }

    if (error && admins.length === 0) {
        return (
            <div className={styles.manageAdminsContainer}>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <button onClick={onBack} className={styles.backButton}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5M12 19l-7-7 7-7" />
                            </svg>
                            Back
                        </button>
                        <h2 className={styles.headerTitle}>Manage Admins</h2>
                    </div>
                </div>
                <div className={styles.errorState}>
                    <div className={styles.errorIcon}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                            <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                    </div>
                    <h3>Error Loading Admins</h3>
                    <p>{error}</p>
                    <button onClick={fetchAdminsData} className={styles.retryButton}>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.manageAdminsContainer}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button onClick={onBack} className={styles.backButton}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        Back
                    </button>
                    <h2 className={styles.headerTitle}>Manage Admins</h2>
                </div>
                <div className={styles.headerRight}>
                    {hasUpdatePermission && (
                        <button onClick={handleAlterPermissions} className={styles.alterPermissionsButton}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 15l8-8" />
                                <path d="M16 3l5 5" />
                                <path d="M8 21l4-4" />
                                <path d="M3 11l9 9" />
                            </svg>
                            Alter Permissions
                        </button>
                    )}
                    {hasCreateAdmins && (
                        <button onClick={handleAddAdmin} className={styles.addAdminButton}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <line x1="19" y1="8" x2="19" y2="14" />
                                <line x1="22" y1="11" x2="16" y2="11" />
                            </svg>
                            Add Admin
                        </button>
                    )}
                </div>
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className={styles.successMessage}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22,4 12,14.01 9,11.01" />
                    </svg>
                    {successMessage}
                </div>
            )}

            {/* Content */}
            <div className={styles.content}>
                {admins.length === 0 ? (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyIcon}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                        </div>
                        <h3>No Admins Found</h3>
                        <p>There are currently no admins to display.</p>
                        {hasCreateAdmins && (
                            <button onClick={handleAddAdmin} className={styles.emptyStateButton}>
                                Add First Admin
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className={styles.adminsGrid}>
                            {admins.map((admin) => (
                                <div key={admin._id} className={styles.adminCard}>
                                    <div className={styles.adminAvatar}>
                                        {admin.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div className={styles.adminInfo}>
                                        <h3 className={styles.adminName}>{admin.name}</h3>
                                        <p className={styles.adminEmail}>{admin.email}</p>
                                        <span className={`${styles.roleBadge} ${getRoleBadgeClass(admin)}`}>
                                            {getRoleDisplay(admin)}
                                        </span>
                                    </div>
                                    <div className={styles.adminActions}>
                                        <button
                                            className={styles.roleButton}
                                            onClick={() => handleChangeRole(admin)}
                                            title="Change Role"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="1" />
                                                <circle cx="12" cy="5" r="1" />
                                                <circle cx="12" cy="19" r="1" />
                                                <circle cx="5" cy="12" r="1" />
                                                <circle cx="19" cy="12" r="1" />
                                            </svg>
                                        </button>
                                        <button
                                            className={`${styles.deleteButton} ${deletingAdminId === admin._id ? styles.deleting : ''}`}
                                            onClick={() => handleDeleteAdmin(admin._id, admin.name)}
                                            disabled={deletingAdminId === admin._id}
                                            title="Delete Admin"
                                        >
                                            {deletingAdminId === admin._id ? (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.spinner}>
                                                    <circle cx="12" cy="12" r="10" />
                                                    <path d="M14 2v6l3 3" />
                                                </svg>
                                            ) : (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="3,6 5,6 21,6" />
                                                    <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
                                                    <line x1="10" y1="11" x2="10" y2="17" />
                                                    <line x1="14" y1="11" x2="14" y2="17" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className={styles.paginationContainer}>
                                <PageNavigator
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        )}

                        {/* Results Info */}
                        <div className={styles.resultsInfo}>
                            Showing {((currentPage - 1) * pagination.limit) + 1} to {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} admins
                        </div>
                    </>
                )}
            </div>

            {/* Edit Admin Dialog */}
            {showRoleDialog && (
                <div className={styles.modalOverlay}>
                    <div className={styles.roleModal}>
                        <div className={styles.modalHeader}>
                            <h3>Edit Admin: {selectedAdmin?.name}</h3>
                            <button onClick={handleCloseRoleDialog} className={styles.closeButton}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        <div className={styles.roleForm}>
                            {roleError && (
                                <div className={styles.formError}>
                                    {roleError}
                                </div>
                            )}

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Name *</label>
                                <input
                                    type="text"
                                    value={editFormData.name}
                                    onChange={(e) => handleEditFormChange('name', e.target.value)}
                                    className={styles.formInput}
                                    placeholder="Enter admin name"
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Email *</label>
                                <input
                                    type="email"
                                    value={editFormData.email}
                                    onChange={(e) => handleEditFormChange('email', e.target.value)}
                                    className={styles.formInput}
                                    placeholder="Enter email address"
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Password</label>
                                <input
                                    type="password"
                                    value={editFormData.password}
                                    onChange={(e) => handleEditFormChange('password', e.target.value)}
                                    className={styles.formInput}
                                    placeholder="Leave empty to keep current password"
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Select Role *</label>
                                <div className={styles.radioGroup}>
                                    {roles.map((role) => (
                                        <label key={role._id} className={styles.radioLabel}>
                                            <input
                                                type="radio"
                                                name="role"
                                                value={role._id}
                                                checked={selectedRole === role._id}
                                                onChange={(e) => handleRoleChange(e.target.value)}
                                                className={styles.radioInput}
                                            />
                                            <span className={styles.radioText}>{role.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className={styles.formActions}>
                                <button
                                    type="button"
                                    onClick={handleCloseRoleDialog}
                                    className={styles.cancelButton}
                                    disabled={roleLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveRole}
                                    className={styles.submitButton}
                                    disabled={roleLoading || !selectedRole}
                                >
                                    {roleLoading ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Alter Permissions Dialog */}
            {showPermissionsDialog && (
                <div className={styles.modalOverlay}>
                    <div className={styles.permissionsModal}>
                        <div className={styles.modalHeader}>
                            <h3>Manage Admin Permissions</h3>
                            <button onClick={handleClosePermissionsDialog} className={styles.closeButton}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        <div className={styles.permissionsForm}>
                            {permissionsError && (
                                <div className={styles.formError}>
                                    {permissionsError}
                                </div>
                            )}

                            <div className={styles.permissionsContainer}>
                                <label className={styles.formLabel}>Select Permissions</label>
                                <div className={styles.permissionsGrid}>
                                    {allPermissions.map((permission) => (
                                        <label key={permission._id} className={styles.checkboxLabel}>
                                            <input
                                                type="checkbox"
                                                checked={selectedPermissions.includes(permission.name)}
                                                onChange={(e) => handlePermissionChange(permission.name, e.target.checked)}
                                                className={styles.checkboxInput}
                                            />
                                            <span className={styles.checkboxText}>
                                                {permission.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className={styles.formActions}>
                                <button
                                    type="button"
                                    onClick={handleClosePermissionsDialog}
                                    className={styles.cancelButton}
                                    disabled={permissionsLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSavePermissions}
                                    className={styles.submitButton}
                                    disabled={permissionsLoading}
                                >
                                    {permissionsLoading ? 'Saving...' : 'Save Permissions'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Admin Form Modal */}
            {showAddForm && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <div className={styles.modalHeader}>
                            <h3>Add New Admin</h3>
                            <button onClick={handleCloseForm} className={styles.closeButton}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSubmitForm} className={styles.form}>
                            {formError && (
                                <div className={styles.formError}>
                                    {formError}
                                </div>
                            )}

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Name *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleFormInputChange('name', e.target.value)}
                                    className={styles.formInput}
                                    placeholder="Enter admin name"
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Email *</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleFormInputChange('email', e.target.value)}
                                    className={styles.formInput}
                                    placeholder="Enter email address"
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Password *</label>
                                <input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => handleFormInputChange('password', e.target.value)}
                                    className={styles.formInput}
                                    placeholder="Enter password (min 6 characters)"
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Role *</label>
                                <select
                                    value={formData.role_id}
                                    onChange={(e) => handleFormInputChange('role_id', e.target.value)}
                                    className={styles.formSelect}
                                    required
                                >
                                    <option value="">Select a role</option>
                                    {roles.map((role) => (
                                        <option key={role._id} value={role._id}>
                                            {role.name.charAt(0).toUpperCase() + role.name.slice(1).replace(/_/g, ' ')}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Phone Code *</label>
                                    <select
                                        value={formData.phone_code}
                                        onChange={(e) => handleFormInputChange('phone_code', e.target.value)}
                                        className={styles.formSelect}
                                        required
                                    >
                                        <option value="+20">+20 (Egypt)</option>
                                        <option value="+966">+966 (Saudi Arabia)</option>
                                        <option value="+971">+971 (UAE)</option>
                                        <option value="+965">+965 (Kuwait)</option>
                                        <option value="+973">+973 (Bahrain)</option>
                                        <option value="+968">+968 (Oman)</option>
                                        <option value="+974">+974 (Qatar)</option>
                                        <option value="+1">+1 (USA/Canada)</option>
                                        <option value="+44">+44 (UK)</option>
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Phone Number *</label>
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => handleFormInputChange('phone', e.target.value)}
                                        className={styles.formInput}
                                        placeholder="Enter phone number"
                                        required
                                    />
                                </div>
                            </div>

                            <div className={styles.formActions}>
                                <button
                                    type="button"
                                    onClick={handleCloseForm}
                                    className={styles.cancelButton}
                                    disabled={formLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className={styles.submitButton}
                                    disabled={formLoading}
                                >
                                    {formLoading ? 'Creating...' : 'Create Admin'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageAdmins;
